import { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";

import { APISuccessResponse, Attachment } from "shared/api/api";
import {
  getAttachment,
  getDownloadAllAttachments,
} from "shared/api/attachments/api";
import { AttachmentType } from "shared/types";

import Button from "features/components/Button";

import AttachmentsModal from "./AttachmentsModal";

interface Props {
  resourceType: AttachmentType;
  itemID: string;
  itemName: string;
  attachments: Attachment[];
  noAttachmentsText?: string;
  noAttachmentsClassName?: string;
}

const createAndDownloadZIPFile = (
  response: APISuccessResponse<Blob>,
  fileName: string,
  contentType?: string | null
): void => {
  const blob = new Blob([response.data], {
    type: contentType || (response.headers["content-type"] as string),
  });

  createAndDownloadFile(blob, fileName);
};

const createAndDownloadFile = (blob: Blob, fileName: string): void => {
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const Attachments = ({
  resourceType,
  itemID,
  itemName,
  attachments,
  noAttachmentsText,
  noAttachmentsClassName,
}: Props) => {
  const { t } = useTranslation("common");
  const validAttachments = attachments.filter(
    ({ url, contentType }) => url && url.length > 0 && contentType
  );

  const [modalOpen, setModalOpen] = useState(false);

  const onShowAll = () => {
    setModalOpen(true);
  };

  const onModalClose = () => {
    setModalOpen(false);
  };

  const onDownloadAttachment = (attachmentID: string) => {
    const attachment = validAttachments.find(({ ID }) => ID === attachmentID);
    if (!attachment || !attachment.url || !attachment.contentType) {
      return;
    }

    getAttachment({ url: attachment.url, contentType: attachment.contentType })
      .then((response) => {
        createAndDownloadFile(response, attachment.filename);
      })
      .catch((e) => {
        console.warn(e);
      });
  };

  const onDownloadAll = () => {
    getDownloadAllAttachments({ resource: resourceType, id: itemID })
      .then((response) => {
        createAndDownloadZIPFile(response, `${resourceType}_documents.zip`);
      })
      .catch((e) => {
        console.warn(e);
      });
  };

  if (validAttachments.length === 0) {
    return (
      <div
        className={classNames(
          "text-sm bg-white w-full p-2 flex justify-center items-center",
          noAttachmentsClassName
        )}
      >
        [{noAttachmentsText ?? t("attachments.none")}]
      </div>
    );
  }

  return (
    <div className="flex flex-row flex-1 gap-3 text-xs !h-12 justify-center items-center">
      <FileCopyOutlinedIcon />
      <Button
        color="secondary"
        variant="contained"
        size="small"
        onClick={onShowAll}
      >
        {t("attachments.view", { count: validAttachments.length })}
      </Button>
      {modalOpen && (
        <AttachmentsModal
          attachments={validAttachments}
          open={modalOpen}
          onClose={onModalClose}
          title={t("attachments.title", { name: itemName })}
          onDownloadAttachment={onDownloadAttachment}
          onDownloadAll={onDownloadAll}
        />
      )}
    </div>
  );
};

export default Attachments;
